import { KeyboardEvent, useState } from "react";
import { IAppliedFilters, IFilterValues } from "../../interfaces/benefit";
import { formatDateInput } from "../../utils/format";
import { ChevronDownIcon, CloseIcon, FilterIcon, FilterOffIcon } from "../ui/Icons";

interface IFiltersPanelProps {
  draftValues: IFilterValues;
  appliedFilters: IAppliedFilters;
  hasActiveFilters: boolean;
  onDraftChange: (key: keyof IFilterValues, value: string) => void;
  onApplyText: (key: keyof IFilterValues, value: string) => void;
  onApplyAnswer: (value: string) => void;
  onRemove: (key: keyof IFilterValues, value: string) => void;
  onClear: () => void;
}

type FilterFieldType = "text" | "select";

interface IFilterField {
  key: keyof IFilterValues;
  label: string;
  placeholder: string;
  type: FilterFieldType;
  /** Los campos de fecha solo aceptan números y se autoformatean a DD-MM-AAAA */
  isDate?: boolean;
}

/** Opciones del filtro de respuesta, compartidas por el select y las pastillas */
const ANSWER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "", label: "Todas" },
  { value: "SI", label: "Sí" },
  { value: "NO", label: "No" },
  { value: "SIN RESPUESTA", label: "Sin respuesta" },
];

const ANSWER_LABELS: Record<string, string> = ANSWER_OPTIONS.reduce(
  (map, option) => ({ ...map, [option.value]: option.label }),
  {} as Record<string, string>
);

const FILTER_FIELDS: IFilterField[] = [
  { key: "DNI", label: "RUT", placeholder: "11222333-4 · Enter para agregar", type: "text" },
  { key: "name", label: "Nombre", placeholder: "Nombre o apellido · Enter", type: "text" },
  { key: "loadDate", label: "Fecha carga", placeholder: "DD-MM-AAAA · Enter", type: "text", isDate: true },
  { key: "sendDate", label: "Fecha envío", placeholder: "DD-MM-AAAA · Enter", type: "text", isDate: true },
  { key: "answerDate", label: "Fecha respuesta", placeholder: "DD-MM-AAAA · Enter", type: "text", isDate: true },
  { key: "answer", label: "Respuesta", placeholder: "", type: "select" },
  { key: "file", label: "Archivo", placeholder: "Archivo · Enter", type: "text" },
];

const FIELD_LABELS: Record<keyof IFilterValues, string> = FILTER_FIELDS.reduce(
  (map, field) => ({ ...map, [field.key]: field.label }),
  {} as Record<keyof IFilterValues, string>
);

/** Texto legible de una pastilla de filtro aplicado */
const chipText = (key: keyof IFilterValues, value: string): string => {
  if (key === "answer") return `${FIELD_LABELS[key]}: ${ANSWER_LABELS[value] ?? value}`;
  return `${FIELD_LABELS[key]}: ${value}`;
};

/**
 * Sección de filtros independiente de la tabla. En los campos de texto se
 * presiona Enter para aplicar el valor y dejar el campo vacío, de modo que se
 * pueden acumular varios valores (varios RUT, varios nombres, etc.).
 */
export default function FiltersPanel({
  draftValues,
  appliedFilters,
  hasActiveFilters,
  onDraftChange,
  onApplyText,
  onApplyAnswer,
  onRemove,
  onClear,
}: IFiltersPanelProps) {
  const [isOpen, setIsOpen] = useState<boolean>(true);

  const handleKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
    key: keyof IFilterValues
  ): void => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    onApplyText(key, draftValues[key]);
  };

  // pares [campo, valor] de todos los filtros aplicados, para las pastillas
  const appliedChips: Array<{ key: keyof IFilterValues; value: string }> = (
    Object.keys(appliedFilters) as Array<keyof IFilterValues>
  ).flatMap((key) => appliedFilters[key].map((value) => ({ key, value })));

  return (
    <section
      className={`filters ${isOpen ? "filters--open" : "filters--closed"}`}
      aria-label="Filtros de la tabla"
    >
      <div className="filters__head">
        <button
          type="button"
          className="filters__toggle"
          onClick={() => setIsOpen((open) => !open)}
          aria-expanded={isOpen}
          aria-controls="filters-body"
        >
          <FilterIcon size={15} />
          <span className="filters__title">Filtros</span>
          <span className="filters__chevron">
            <ChevronDownIcon size={16} />
          </span>
        </button>

        {hasActiveFilters && (
          <button type="button" className="btn btn--sm btn--ghost" onClick={onClear}>
            <FilterOffIcon size={14} />
            Limpiar filtros
          </button>
        )}
      </div>

      <div className="filters__body" id="filters-body">
        <div className="filters__body-inner">
          <div className="filters__grid">
            {FILTER_FIELDS.map((field) => (
              <label className="filters__field" key={field.key}>
                <span className="filters__label">{field.label}</span>

                {field.type === "select" ? (
                  <select
                    className="filter-select"
                    value={draftValues[field.key]}
                    onChange={(event) => onApplyAnswer(event.target.value)}
                  >
                    {ANSWER_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="filter-input"
                    type="text"
                    value={draftValues[field.key]}
                    placeholder={field.placeholder}
                    inputMode={field.isDate ? "numeric" : undefined}
                    maxLength={field.isDate ? 10 : undefined}
                    onChange={(event) =>
                      onDraftChange(
                        field.key,
                        field.isDate ? formatDateInput(event.target.value) : event.target.value
                      )
                    }
                    onKeyDown={(event) => handleKeyDown(event, field.key)}
                  />
                )}
              </label>
            ))}
          </div>

          <div className="filters__applied">
            {appliedChips.length === 0 ? (
              <span className="filters__empty">No hay filtros aplicados</span>
            ) : (
              <>
                <span className="filters__applied-label">Aplicados:</span>
                {appliedChips.map(({ key, value }) => (
                  <span className="chip" key={`${key}-${value}`}>
                    {chipText(key, value)}
                    <button
                      type="button"
                      className="chip__remove"
                      onClick={() => onRemove(key, value)}
                      aria-label={`Quitar filtro ${FIELD_LABELS[key]}: ${value}`}
                    >
                      <CloseIcon size={12} />
                    </button>
                  </span>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
